// ============================================================================
// ContactForm — formulaire unifié de contact / signalement
// ============================================================================
// Utilisé par la page publique /contact (l'ex-bouton flottant SupportFAB a été
// remplacé par QuickActionsFAB, déposé au grand nettoyage 2026-08-15).
// 5 types de demande : renseignement, incident en séance, suggestion,
// bug technique, demande pédagogique / commerciale.
//
// - Pour un visiteur non authentifié : nom, prénom et email requis.
// - Pour un utilisateur authentifié : les coordonnées sont pré-remplies et
//   masquées, userId/organisationId sont ajoutés côté serveur depuis le JWT.
// - Le type INCIDENT_SEANCE expose un sélecteur de groupe parmi les groupes
//   auxquels l'utilisateur participe ou anime (rattachement animateur /
//   stagiaire). En mode public anonyme, on demande simplement de décrire
//   l'incident dans la description.
// ============================================================================

import { useState, useMemo } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Stack,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { HelpCircle, AlertTriangle, Lightbulb, Bug, GraduationCap, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { getAttribution } from "../lib/attribution";

export type DemandeType =
  | "RENSEIGNEMENT"
  | "INCIDENT_SEANCE"
  | "SUGGESTION"
  | "BUG_TECHNIQUE"
  | "PEDAGOGIQUE_COMMERCIAL";

interface TypeConfig {
  label: string;
  description: string;
  icon: typeof HelpCircle;
  color: string;
  objetPlaceholder: string;
}

export const TYPE_CONFIG: Record<DemandeType, TypeConfig> = {
  RENSEIGNEMENT: {
    label: "Demande de renseignement",
    description: "Une question sur nos formations, le fonctionnement, l'inscription…",
    icon: HelpCircle,
    color: "#0284c7",
    objetPlaceholder: "Ex : Conditions d'accès à la formation Codev…",
  },
  INCIDENT_SEANCE: {
    label: "Signaler un incident en séance",
    description: "Un événement survenu pendant une séance qui mérite notre attention.",
    icon: AlertTriangle,
    color: "#d97706",
    objetPlaceholder: "Ex : Problème de connexion visio le 12 mars…",
  },
  SUGGESTION: {
    label: "Faire une suggestion",
    description: "Une idée pour améliorer nos formations ou notre plateforme.",
    icon: Lightbulb,
    color: "#16a34a",
    objetPlaceholder: "Ex : Ajouter un export PDF des évaluations…",
  },
  BUG_TECHNIQUE: {
    label: "Signaler un bug technique",
    description: "Un dysfonctionnement de l'outil (erreur, page bloquée, bouton inactif…).",
    icon: Bug,
    color: "#dc2626",
    objetPlaceholder: "Ex : Impossible de valider l'émargement…",
  },
  PEDAGOGIQUE_COMMERCIAL: {
    label: "Demande pédagogique ou commerciale",
    description: "Devis, accompagnement sur mesure, partenariat pédagogique.",
    icon: GraduationCap,
    color: "#7c3aed",
    objetPlaceholder: "Ex : Devis pour 3 groupes Codev en entreprise…",
  },
};

interface ContactFormProps {
  initialType?: DemandeType;
  onSuccess?: () => void;
  /** Affiche un titre par défaut. Passez `false` pour gérer le titre côté parent. */
  showHeader?: boolean;
}

export function ContactForm({
  initialType = "RENSEIGNEMENT",
  onSuccess,
  showHeader = true,
}: ContactFormProps) {
  const { user } = useAuth();

  const [type, setType] = useState<DemandeType>(initialType);
  const [objet, setObjet] = useState("");
  const [description, setDescription] = useState("");
  const [contactNom, setContactNom] = useState("");
  const [contactPrenom, setContactPrenom] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactTelephone, setContactTelephone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const typeCfg = TYPE_CONFIG[type];
  const Icon = typeCfg.icon;

  const coordonneesValides = useMemo(() => {
    if (user) return true;
    return (
      contactEmail.trim().length > 0 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(contactEmail.trim())
    );
  }, [user, contactEmail]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!objet.trim() || !description.trim()) {
      toast.error("L'objet et la description sont requis");
      return;
    }
    if (!user && !coordonneesValides) {
      toast.error("Un email de contact valide est requis");
      return;
    }

    try {
      setSubmitting(true);
      await api.post("/support", {
        type,
        objet: objet.trim(),
        description: description.trim(),
        contactNom: contactNom.trim() || null,
        contactPrenom: contactPrenom.trim() || null,
        contactEmail: contactEmail.trim() || null,
        contactTelephone: contactTelephone.trim() || null,
        // Attribution marketing first-touch (CRM C1.2) — suit la demande
        // jusqu'à l'affaire à la qualification.
        attribution: getAttribution(),
      });
      toast.success("Merci ! Votre message a bien été transmis à l'équipe CECODEV.");
      setObjet("");
      setDescription("");
      if (!user) {
        setContactNom("");
        setContactPrenom("");
        setContactEmail("");
        setContactTelephone("");
      }
      onSuccess?.();
    } catch (err) {
      console.error("Erreur envoi demande:", err);
      toast.error("Erreur lors de l'envoi — veuillez réessayer");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2.5}>
        {showHeader && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: `${typeCfg.color}18`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Icon size={22} color={typeCfg.color} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {typeCfg.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {typeCfg.description}
              </Typography>
            </Box>
          </Box>
        )}

        <TextField
          select
          label="Type de demande"
          size="small"
          value={type}
          onChange={(e) => setType(e.target.value as DemandeType)}
          fullWidth
        >
          {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
            const I = cfg.icon;
            return (
              <MenuItem key={key} value={key}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <I size={16} color={cfg.color} />
                  <span>{cfg.label}</span>
                </Box>
              </MenuItem>
            );
          })}
        </TextField>

        {!user && (
          <Alert severity="info" sx={{ fontSize: "0.8125rem" }}>
            Vous n'êtes pas connecté. Merci de renseigner un email valide pour que
            nous puissions vous répondre.
          </Alert>
        )}

        {!user && (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Prénom"
              size="small"
              value={contactPrenom}
              onChange={(e) => setContactPrenom(e.target.value)}
              fullWidth
            />
            <TextField
              label="Nom"
              size="small"
              value={contactNom}
              onChange={(e) => setContactNom(e.target.value)}
              fullWidth
            />
          </Stack>
        )}

        {!user && (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Email"
              type="email"
              size="small"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Téléphone (facultatif)"
              size="small"
              value={contactTelephone}
              onChange={(e) => setContactTelephone(e.target.value)}
              fullWidth
            />
          </Stack>
        )}

        {type === "INCIDENT_SEANCE" && (
          <Alert severity="warning" sx={{ fontSize: "0.8125rem" }}>
            Merci de préciser dans votre message : la date, la séance et le groupe concernés
            ainsi que le déroulé de l'incident. L'équipe CECODEV reviendra vers vous rapidement.
          </Alert>
        )}

        <TextField
          label="Objet"
          size="small"
          value={objet}
          onChange={(e) => setObjet(e.target.value)}
          placeholder={typeCfg.objetPlaceholder}
          required
          fullWidth
          inputProps={{ maxLength: 255 }}
        />

        <TextField
          label="Votre message"
          multiline
          rows={6}
          size="small"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          fullWidth
          placeholder={
            type === "INCIDENT_SEANCE"
              ? "Décrivez ce qui s'est passé : date, séance, personnes impliquées, contexte…"
              : "Décrivez votre demande en quelques lignes…"
          }
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || !objet.trim() || !description.trim() || !coordonneesValides}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Send size={16} />}
            sx={{
              bgcolor: typeCfg.color,
              "&:hover": { bgcolor: typeCfg.color, filter: "brightness(0.92)" },
            }}
          >
            {submitting ? "Envoi…" : "Envoyer la demande"}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
